import React, {useState} from "react";
import {useTranslation} from "react-i18next";

import "./style.less";

import {usePlatform} from "../../../../../../../../context/PlatformContext";
import {platformServices} from "../../../../../../../../services";
import Loading from "../../../../../../../../components/Loading";
import {findPopupConflict} from "../../../../../../../../utils/popups";
import PopupEditorDialog from "./PopupEditorDialog";

const T = "config.platform.customizable_popups";

const CustomizablePopups = () => {

    const {t} = useTranslation();
    const {platform, refreshPlatform} = usePlatform();

    const savedPopups = platform?.functionalities_extras?.customizable_popups?.popups || [];

    const [editing, setEditing] = useState(null); // null | popup object (existing or {} for new)
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Validate + persist the whole popups list, then refresh the platform so the
    // context reflects the (backend-normalized, id-assigned) result. Returns an
    // error key on failure or null on success. The client-side conflict check
    // mirrors the backend so the editor dialog can surface it immediately.
    const persist = async (nextPopups) => {
        if (findPopupConflict(nextPopups)) return "err-popup_conflict";
        setSaving(true);
        try {
            await platformServices.saveCustomizablePopups(nextPopups);
            await refreshPlatform();
            return null;
        } catch (e) {
            return e?.error?.message || e?.message || "save_failed";
        } finally {
            setSaving(false);
        }
    };

    // Returns the error key (shown inside the dialog) or null on success.
    const handleSave = async (popup) => {
        const exists = savedPopups.some(p => p.id && p.id === popup.id);
        const next = exists
            ? savedPopups.map(p => p.id === popup.id ? popup : p)
            : [...savedPopups, popup];
        const err = await persist(next);
        if (!err) setEditing(null);
        return err;
    };

    const handleDelete = async (id) => {
        setError(null);
        const err = await persist(savedPopups.filter(p => p.id !== id));
        if (err) setError(err);
    };

    const locationsLabel = (locations) => (locations || []).map(l => t(`${T}.locations.${l}`)).join(", ");

    const dateSummary = (p) => {
        if (!p.start_date && !p.end_date) return t(`${T}.summary.always`);
        if (p.start_date && p.end_date) return `${p.start_date} → ${p.end_date}`;
        if (p.start_date) return t(`${T}.summary.from`, {date: p.start_date});
        return t(`${T}.summary.until`, {date: p.end_date});
    };

    return (
        <section className="CustomizablePopups basic_container">
            <header>
                <h3>{t(`${T}.title`)}</h3>
                <p>{t(`${T}.description`)}</p>
            </header>

            <div className="CustomizablePopups__list">
                {savedPopups.length === 0 && (
                    <p className="CustomizablePopups__empty">{t(`${T}.empty`)}</p>
                )}
                {savedPopups.map(p => (
                    <div className="CustomizablePopups__card basic_container" key={p.id}>
                        <div className="CustomizablePopups__card-main">
                            <div className="CustomizablePopups__card-title">
                                <strong>{p.name || t(`${T}.untitled`)}</strong>
                                <span className={"CustomizablePopups__badge" + (p.active ? " is-active" : "")}>
                                    {p.active ? t(`${T}.summary.active`) : t(`${T}.summary.inactive`)}
                                </span>
                            </div>
                            <small>{locationsLabel(p.locations)} · {dateSummary(p)}</small>
                        </div>
                        <div className="CustomizablePopups__card-actions">
                            <button type="button" className="btn btn-tertiary" disabled={saving}
                                    onClick={() => { setError(null); setEditing(p); }}>
                                {t("buttons.edit")}
                            </button>
                            <button type="button" className="btn btn-tertiary" disabled={saving}
                                    onClick={() => handleDelete(p.id)}>
                                {t("buttons.remove")}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <button type="button" className="btn btn-secondary CustomizablePopups__add-btn"
                    disabled={saving} onClick={() => { setError(null); setEditing({}); }}>
                {`+ ${t(`${T}.add_popup`)}`}
            </button>

            {error && <p className="error_message">
                {t(`${T}.errors.${error}`, t(`${T}.errors.save_failed`))}
            </p>}

            {editing !== null && (
                <PopupEditorDialog
                    key={editing.id || "new"}
                    popup={editing}
                    saving={saving}
                    onCancel={() => { setEditing(null); setError(null); }}
                    onSave={handleSave}
                />
            )}

            {saving && <Loading cover={0.3}/>}
        </section>
    );
};

export default CustomizablePopups;
